/**
 * Utility functions for handling dates in various formats
 */

/**
 * Parse EXIF date format (YYYY:MM:DD HH:MM:SS) to a Date object
 * Handles multiple formats:
 * - Standard EXIF: YYYY:MM:DD HH:MM:SS
 * - Extended EXIF with milliseconds: YYYY:MM:DD HH:MM:SS.ms
 * - Extended EXIF with timezone: YYYY:MM:DD HH:MM:SS±HH:MM
 * - Extended EXIF with milliseconds and timezone: YYYY:MM:DD HH:MM:SS.ms±HH:MM
 * @param exifDateString - Date string in EXIF format
 * @returns Date object or null if parsing fails
 */
export const parseExifDate = (exifDateString: string): Date | null => {
  if (!exifDateString) return null;
  
  try {
    // Standard EXIF format (YYYY:MM:DD HH:MM:SS)
    const basicExifPattern = /^(\d{4}):(\d{2}):(\d{2})\s(\d{2}):(\d{2}):(\d{2})$/;
    
    // Extended EXIF format with milliseconds and optional timezone
    // Matches: YYYY:MM:DD HH:MM:SS.ms±HH:MM or YYYY:MM:DD HH:MM:SS.ms
    const extendedExifPattern = /^(\d{4}):(\d{2}):(\d{2})\s(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(?:([+-])(\d{2}):(\d{2}))?$/;
    
    // Try extended pattern first (more specific)
    const extendedMatch = exifDateString.match(extendedExifPattern);
    if (extendedMatch) {
      // Extract components from regex match
      const [, year, month, day, hour, minute, second, ms, tzSign, tzHour, tzMinute] = extendedMatch;
      
      // Create ISO 8601 format string which JS Date handles well
      let isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
      
      // Add milliseconds if present
      if (ms) {
        isoString += `.${ms}`;
      }
      
      // Add timezone if present, otherwise assume local timezone
      if (tzSign && tzHour && tzMinute) {
        isoString += `${tzSign}${tzHour}:${tzMinute}`;
      }
      
      const date = new Date(isoString);
      return !isNaN(date.getTime()) ? date : null;
    }
    
    // Try basic pattern if extended didn't match
    const basicMatch = exifDateString.match(basicExifPattern);
    if (basicMatch) {
      // Extract components from regex match
      const [, year, month, day, hour, minute, second] = basicMatch;
      // Note: month is 0-indexed in JavaScript Date
      return new Date(
        parseInt(year), 
        parseInt(month) - 1, 
        parseInt(day), 
        parseInt(hour), 
        parseInt(minute), 
        parseInt(second)
      );
    }
    
    // If neither pattern matched, try standard ISO format
    const date = new Date(exifDateString);
    return !isNaN(date.getTime()) ? date : null;
  } catch (error) {
    console.error('Error parsing date:', exifDateString, error);
    return null;
  }
};

/**
 * Format a date to EXIF format (YYYY:MM:DD HH:MM:SS)
 * @param date - Date object to format
 * @returns String in EXIF format
 */
export const formatToExifDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  
  return `${year}:${month}:${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Format a date string to a human-readable format
 * @param dateString - Date string (EXIF or ISO format)
 * @returns Formatted date string or fallback message if invalid
 */
export const formatDateForDisplay = (dateString: string | undefined): string => {
  if (!dateString) return 'Date unknown';
  
  try {
    const date = parseExifDate(dateString);
    
    if (!date) {
      console.error('Could not parse date:', dateString);
      return 'Invalid date format';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error parsing date';
  }
};

/**
 * Create time string in HH:MM format from a Date object
 * @param date - Date object
 * @returns Time string in HH:MM format
 */
export const formatTimeString = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Apply time string (HH:MM) to a Date object
 * @param date - Date object to modify
 * @param timeString - Time string in HH:MM format
 * @returns New Date object with updated time
 */
export const applyTimeToDate = (date: Date, timeString: string): Date => {
  const newDate = new Date(date);
  const [hours, minutes] = timeString.split(':').map(Number);
  newDate.setHours(hours || 0);
  newDate.setMinutes(minutes || 0);
  newDate.setSeconds(0);
  return newDate;
};
